import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'gatsby';
import { navigate } from "gatsby"

import Layout from '../../../src/components/Layout';
import Search from '../../../static/images/documentation/search.inline.svg';
import ChevronDown from '../../../static/images/documentation/chevron-down.inline.svg';
import AmbassadorCloud from '../../../static/images/documentation/ambassador-cloud.inline.svg';
import RightArrow from '../../../static/images/documentation/right-arrow.inline.svg';
import TelepresenceIcon from '../../../static/images/documentation/telepresence-icon.inline.svg';
import EdgeStackIcon from '../../../static/images/documentation/edge-stack-icon.inline.svg';
import SlackIcon from '../../../static/images/documentation/slack-icon.inline.svg';
import MailIcon from '../../../static/images/documentation/mail-icon.inline.svg';
import GithubIcon from '../../../static/images/documentation/github-icon.inline.svg';
import {
    goToGetAmbassador, goToTelepresenceDocs, goToTutorialsGettingStarted, goToTelepresenceHowTo, goToTelepresenceEnv,
    goToTelepresenceOutbound, goToMappings, goToOauth, goToRateLimits, goToSlack, goToContactUs, goToGithubDocs
} from '../../utils/routes';
import { products } from '../../utils/documentationConfig';
import './style.less';

const DocumentationLanding = ({ location }) => {

    const [product, setProduct] = useState(products[0].name);
    const [version, setVersion] = useState("");
    const [showVersion, setShowVersion] = useState(false);
    const [versionList, setVersionList] = useState([]);

    useEffect(() => {
        loadJS();
    }, []);

    const handleProductChange = (e, name = null) => {
        const value = name ? name : e.target.value;
        const selectedProduct = products.filter(p => p.name === value)[0];
        navigate(selectedProduct.link);

        // This will be enable again with the new documentation structure
        // setProduct(value);
        // setShowVersion(value !== products[0].name);
        // setVersionList(selectedProduct.version);
        // setVersion(selectedProduct.version[0]);
    }
    const handleVersionChange = (e) => setVersion(e.target.value);

    const loadJS = () => {
        if (window.docsearch) {
            window.docsearch({
                apiKey: '8f887d5b28fbb0aeb4b98fd3c4350cbd',
                indexName: 'getambassador',
                inputSelector: '#doc-search',
                debug: true,
            });
        } else {
            setTimeout(() => {
                loadJS();
            }, 500);
        }
    }

    return (
        <Layout location={location}>
            <Helmet>
                <title>Docs Home | Ambassador</title>
                <meta name="description" content="Docs Home | Ambassador" />
                <meta name="keywords" content="Ambassador, Documentation, Telepresence, Edge Stack" />
                <meta name="og:type" content="article" />
                <meta name="author" content="Datawire.io" />
            </Helmet>
            <div className="docs">
                <nav>
                    <div className="docs__nav">
                        <div className="docs__links-content docs__dekstop">
                            <ul className="docs__products-list">
                                {products.map(item => (
                                    <li
                                        className={`${product === item.name ? 'docs__selected' : ''}`}
                                        onClick={(e) => handleProductChange(e, item.name)}
                                        key={item.name}
                                    >
                                        <Link to={item.link}>{item.name}</Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className={`docs__dropdown-container docs__mobile${showVersion ? ' docs__dropdown-version' : ''}`}>
                            <div className="docs__dropdown">
                                <label>{product}</label>
                                <ChevronDown className="docs__chevdown" />
                                <select onChange={handleProductChange} value={product}>
                                    {products.map(item => (
                                        <option
                                            value={item.name}
                                            key={`option-${item.name}`}
                                        >{item.name}</option>
                                    ))}
                                </select>
                            </div>
                            {showVersion && <div className="docs__dropdown">
                                <label>Version: {version}</label>
                                <ChevronDown className="docs__chevdown" />
                                <select onChange={handleVersionChange}>
                                    {versionList.map(version => (
                                        <option value={version} key={`${product}-${version}`}>{version}</option>
                                    ))}
                                </select>
                            </div>}
                        </div>
                        <div className="docs__search-box">
                            <Search />
                            <input name="search" type="text" placeholder="Search documentation" id="doc-search" />
                        </div>
                    </div>
                </nav>

                <section className="docs__started docs__container">
                    <h2 className="docs__heading-primary">Documentation</h2>
                    <p className="docs__text-primary">Concepts, guides, and examples to using Ambassador.</p>
                    <div className="docs__info">
                        <div className="docs__info--first">
                            <span className="docs__heading-secondary">Ambassador Cloud</span>
                            <p>Adopt an integrated workflow for quickly coding, testing, and releasing microservices on Kubernetes.</p>
                            <Link to={goToGetAmbassador} className="docs__button-secondary">
                                Get Started <RightArrow className="docs__button-secondary--arrow" />
                            </Link>
                        </div>
                        <div className="docs__info--second">
                            <AmbassadorCloud />
                        </div>
                    </div>
                </section>

                <hr className="docs__separator docs__container" />

                <section className="docs__container">
                    <span className="docs__heading-secondary">Explore Ambassador Products</span>
                    <div className="docs__cards">
                        <div className="docs__card">
                            <div className="docs__card--heading">
                                <TelepresenceIcon />
                                Telepresence
                            </div>
                            <p>Code and test microservices locally against a remote Kubernetes cluster.</p>
                            <Link to={goToTelepresenceDocs} className="docs__button-tertiary">Get Started <RightArrow className="docs__button-tertiary--arrow" /></Link>
                            <hr className="docs__card--separator" />
                            <span className="docs__heading-tertiary">Popular Tutorials</span>
                            <ul className="docs__popular-list">
                                <li><Link to={goToTelepresenceHowTo}>Collaborating with Preview URLs</Link></li>
                                <li><Link to={goToTelepresenceEnv}>Importing Environment Variables</Link></li>
                                <li><Link to={goToTelepresenceOutbound}>Outbound Sessions</Link></li>
                            </ul>
                        </div>
                        <div className="docs__card">
                            <div className="docs__card--heading">
                                <EdgeStackIcon />
                                Edge Stack
                            </div>
                            <p>Route and secure traffic to your cluster with a Kubernetes-native API Gateway.</p>
                            <Link to={goToTutorialsGettingStarted} className="docs__button-tertiary">Get Started <RightArrow className="docs__button-tertiary--arrow" /></Link>
                            <hr className="docs__card--separator" />
                            <span className="docs__heading-tertiary">Popular Tutorials</span>
                            <ul className="docs__popular-list">
                                <li><Link to={goToMappings}>Intro to Mappings</Link></li>
                                <li><Link to={goToOauth}>Single Sign-On Authentication</Link></li>
                                <li><Link to={goToRateLimits}>Rate Limiting</Link></li>
                            </ul>
                        </div>
                    </div>
                </section>

                <hr className="docs__separator docs__container" />

                <section className="docs__contact docs__container">
                    <span className="docs__heading-secondary">Questions?</span>
                    <p>We’re here to help if you have questions.</p>
                    <ul className="docs__contact-list">
                        <li><Link to={goToSlack}><SlackIcon className="docs__contact-list--icon" />Join our Slack</Link></li>
                        <li><Link to={goToContactUs}><MailIcon className="docs__contact-list--icon" />Contact Us</Link></li>
                    </ul>
                </section>

                {/* <footer className="docs__footer">
                    <Link to={goToGithubDocs} target="_blank">
                        <GithubIcon className="docs__footer--icon" />
                        Edit this page on GitHub
                    </Link>
                </footer> */}
            </div>
        </Layout>
    );
};

export default DocumentationLanding;